import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, X, Copy, FileDown } from 'lucide-react';
import { renderField } from './renderField';

// Pure presentational psychotherapy note card.
export function PsychotherapyNoteCard({ note, onClose, onCopy, onCopyCode, onExport }) {
  return (
    <Card
      className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 backdrop-blur-xl rounded-3xl border border-indigo-200 shadow-xl"
      data-testid="psychotherapy-note-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-bold text-gray-900">Psychotherapy Note</h3>
        </div>
        <Button onClick={onClose} variant="ghost" size="sm" className="rounded-full">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          onClick={() => onCopy(note.full_note, 'Psychotherapy note')}
          size="sm"
          variant="outline"
          data-testid="copy-psych-note-button"
          className="rounded-lg text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-100"
        >
          <Copy className="w-3 h-3 mr-1" />
          Copy
        </Button>
        <Button
          onClick={onExport}
          size="sm"
          variant="outline"
          data-testid="export-psych-note-button"
          className="rounded-lg text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-100"
        >
          <FileDown className="w-3 h-3 mr-1" />
          Export
        </Button>
      </div>

      {(note.icd10_codes?.length > 0 || note.cpt_codes?.length > 0) && (
        <div className="mb-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-700 uppercase mb-2 flex items-center gap-2">
            <span>💳</span> Billing Codes
          </p>

          {note.icd10_codes?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1">ICD-10:</p>
              <div className="space-y-1">
                {note.icd10_codes.map((code, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                    <span className="font-mono font-semibold text-green-700">{code.code}</span>
                    <span className="text-gray-700 flex-1 mx-2">{code.description}</span>
                    <button
                      onClick={() => onCopyCode(code.code, 'Code')}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {note.cpt_codes?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">CPT:</p>
              <div className="space-y-1">
                {note.cpt_codes.map((code, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                    <span className="font-mono font-semibold text-blue-700">{code.code}</span>
                    <span className="text-gray-700 flex-1 mx-2">{code.description}</span>
                    <button
                      onClick={() => onCopyCode(code.code, 'Code')}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Presenting Concerns</p>
          <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(note.presenting_concerns)}</p>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Mood & Affect</p>
          <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(note.client_mood_affect)}</p>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Interventions Used</p>
          <div className="space-y-2">
            {Array.isArray(note.interventions)
              ? note.interventions.map((intervention, idx) => (
                  <div key={idx} className="bg-white/60 p-3 rounded-xl text-sm">
                    {typeof intervention === 'object' && intervention !== null ? (
                      <>
                        {intervention.technique && <p className="font-semibold text-indigo-700">{renderField(intervention.technique)}</p>}
                        {intervention.description && <p className="text-gray-700 mt-1">{renderField(intervention.description)}</p>}
                        {intervention.client_response && <p className="text-gray-600 text-xs mt-1 italic">Response: {renderField(intervention.client_response)}</p>}
                      </>
                    ) : (
                      <p className="text-gray-900">{renderField(intervention)}</p>
                    )}
                  </div>
                ))
              : note.interventions && (
                  <div className="bg-white/60 p-3 rounded-xl text-sm text-gray-900 whitespace-pre-wrap">{renderField(note.interventions)}</div>
                )}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Session Goals</p>
          <ul className="bg-white/60 p-3 rounded-xl text-sm space-y-1">
            {Array.isArray(note.session_goals) && note.session_goals.map((goal, idx) => (
              <li key={idx} className="text-gray-900">• {renderField(goal)}</li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Treatment Goals</p>
          <div className="space-y-2">
            {Array.isArray(note.treatment_goals) && note.treatment_goals.map((goal, idx) => (
              <div key={idx} className="bg-white/60 p-3 rounded-xl text-sm">
                {typeof goal === 'object' && goal !== null ? (
                  <>
                    <p className="text-gray-900">{renderField(goal.goal || goal.description || goal)}</p>
                    {goal.progress && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          goal.progress === 'achieved' ? 'bg-green-100 text-green-700' :
                          goal.progress === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {String(goal.progress).replace('_', ' ')}
                        </span>
                        {goal.target_date && <span className="text-xs text-gray-600">Target: {renderField(goal.target_date)}</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-gray-900">{renderField(goal)}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {note.homework_assigned && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Homework</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(note.homework_assigned)}</p>
          </div>
        )}

        {note.risk_assessment && (
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase mb-1">Risk Assessment</p>
            <p className="text-sm text-gray-900 bg-white/60 p-3 rounded-xl">{renderField(note.risk_assessment)}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
